import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import useServerStateContext from '@/shared/hooks/useServerStateContext';
import { AutoIsoObjectType } from '@/features/autoIso/types';
import { usePTZStore } from '@/features/camera/store/usePTZStore';
import { onManualSteer } from '@/shared/hooks/websocket/usePTZSocket';
import { aimAt, heightAtVideoPoint, projectToVideo, wrapDegrees } from '../geometry';
import { PTZValues } from '../types';
import { HIGH_TARGET_FT, MAX_TARGET_HEIGHT, framingTarget } from '../store/framingTargetStore';
import { useTargetAiming } from '../hooks/useTargetAiming';
import { useDriveToPTZ } from '../hooks/useDriveToPTZ';

const DOT_SIZE = 18;
const TARGET_COLOR = '#ffb020';
// The state socket reports the vcam about half a second before the WebRTC
// picture shows it (measured on a dev player: the dot led the ice by 0.5 s
// while panning). Drawing from the vcam that long ago keeps the dot on the
// ice while the camera moves; at rest the two agree anyway.
const VIDEO_LAG_MS = 500;
const HISTORY_MS = 2000;
// Arrow keys on the dot, in feet (Shift for the larger step).
const KEY_STEP_FT = 1;
const KEY_STEP_LARGE_FT = 5;
// A drive that gives up with the dot this far from the centre (a fraction of
// the frame) was held back by the player: the target is out of reach.
const UNREACHABLE_OFFSET = 0.05;
// Zooming in by this much (degrees of field of view) tries an unreachable
// target again: a narrower frame can look higher before it leaves the sensor.
const RETRY_ZOOM_DEG = 1;
// How close to the video's edges the dot is pinned when its spot is off the
// frame (above the player controls at the bottom), and past which fraction of
// the width its label flips to the left.
const EDGE_INSET_X = 0.012;
const EDGE_INSET_TOP = 0.03;
const EDGE_INSET_BOTTOM = 0.12;
const LABEL_FLIP_X = 0.75;

interface Sample {
  t: number;
  ptz: PTZValues;
}

/** The vcam at time `t`, interpolated between the samples around it. */
function vcamAt(history: Sample[], t: number): PTZValues | null {
  if (!history.length) return null;
  if (t <= history[0].t) return history[0].ptz;
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i].t > t) continue;
    const a = history[i];
    const b = history[i + 1];
    if (!b) return a.ptz;
    const k = (t - a.t) / (b.t - a.t);
    return {
      pan: a.ptz.pan + wrapDegrees(b.ptz.pan - a.ptz.pan) * k,
      tilt: a.ptz.tilt + (b.ptz.tilt - a.ptz.tilt) * k,
      zoom: a.ptz.zoom + (b.ptz.zoom - a.ptz.zoom) * k,
    };
  }
  return history[0].ptz;
}

interface Projected {
  dot: { x: number; y: number };
  ground: { x: number; y: number } | null;
}

/**
 * The preset target on the video: a dot standing on a line down to the ice,
 * so its height reads at a glance. Drag the dot up or down (or use the arrow
 * keys) to raise or lower the target; the camera holds still while dragging
 * and re-aims on release. Also keeps the camera aimed at the target,
 * whichever camera is selected.
 */
export const TargetVideoOverlay: React.FC = () => {
  const { aiming, target, dragging, preset, unreachable } = useTargetAiming();
  const { selectedCamera, selectedObjectType } = useServerStateContext();
  const { getCurrentPTZ } = usePTZStore();
  // usePTZStore hands out a new getter every render; the projection loop
  // below must not restart (and lose its history) because of that.
  const readRef = useRef(getCurrentPTZ);
  readRef.current = getCurrentPTZ;
  const { driveTo, stop } = useDriveToPTZ();
  const stopRef = useRef(stop);
  stopRef.current = stop;
  const rootRef = useRef<HTMLDivElement>(null);
  const dotRef = useRef<HTMLDivElement>(null);
  const [projected, setProjected] = useState<Projected | null>(null);
  // Recent vcam samples for the selected camera. Kept across target changes,
  // so moving the target doesn't lose the lag compensation.
  const historyRef = useRef<{ cameraId: number | null; samples: Sample[] }>({
    cameraId: null,
    samples: [],
  });

  useEffect(() => {
    if (!aiming) stop();
  }, [aiming, stop]);

  // Switching to Auto-ISO ends target mode: the rink picks who to follow
  // again. Only a switch away from Manual counts, so entering target mode
  // from Auto-ISO (which asks for Manual) doesn't end it straight away.
  const objectTypeRef = useRef(selectedObjectType);
  useEffect(() => {
    const was = objectTypeRef.current;
    objectTypeRef.current = selectedObjectType;
    if (was === AutoIsoObjectType.None && selectedObjectType !== AutoIsoObjectType.None) {
      framingTarget.end();
    }
  }, [selectedObjectType]);

  // A person steering the camera wins: the frame no longer shows the target,
  // so the target section asks for a new spot (or closes, once applied).
  useEffect(
    () =>
      onManualSteer(() => {
        if (!framingTarget.get().target) return;
        stopRef.current();
        framingTarget.release();
      }),
    [],
  );

  // Zoom when the camera last stopped short of the target.
  const unreachableZoomRef = useRef(0);

  // Out of reach for one camera says nothing about the next: let it try.
  const cameraId = selectedCamera?.id;
  useEffect(() => {
    if (framingTarget.get().unreachable) framingTarget.setUnreachable(false);
  }, [cameraId]);

  // Aim. Every input change hands the drive a new goal; zoom stays the user's.
  // A drive that gives up short of the target (not replaced or interrupted)
  // means the player won't frame it: say so rather than sit off-centre.
  useEffect(() => {
    if (!aiming || !selectedCamera || dragging || unreachable) return;
    if (!target) {
      stop();
      return;
    }
    // A newer aim (another target or camera) owns the outcome from here.
    let current = true;
    driveTo(aimAt(selectedCamera, target)).then((settled) => {
      const now = framingTarget.get();
      const ptz = readRef.current?.();
      if (!current || settled || now.target !== target || now.dragging || !ptz) return;
      const dot = projectToVideo(selectedCamera, ptz, target);
      if (dot && Math.hypot(dot.x - 0.5, dot.y - 0.5) < UNREACHABLE_OFFSET) return;
      unreachableZoomRef.current = ptz.zoom;
      framingTarget.setUnreachable(true);
    });
    return () => {
      current = false;
    };
  }, [aiming, target, selectedCamera, dragging, unreachable, driveTo, stop]);

  // Follow the live vcam so the dot stays on the ice while the camera moves.
  useEffect(() => {
    if (!aiming || !target || !selectedCamera) {
      setProjected(null);
      return;
    }
    if (historyRef.current.cameraId !== selectedCamera.id) {
      historyRef.current = { cameraId: selectedCamera.id, samples: [] };
    }
    const history = historyRef.current.samples;
    let frame = 0;
    const update = () => {
      const now = performance.now();
      const live = readRef.current?.();
      const last = history[history.length - 1];
      if (
        live &&
        (!last ||
          last.ptz.pan !== live.pan ||
          last.ptz.tilt !== live.tilt ||
          last.ptz.zoom !== live.zoom)
      ) {
        history.push({ t: now, ptz: { pan: live.pan, tilt: live.tilt, zoom: live.zoom } });
        if (framingTarget.get().unreachable) {
          // The state socket can go quiet mid-move and look like a stall: if
          // the camera got there after all, there is nothing to warn about.
          const centre = projectToVideo(selectedCamera, live, target);
          const arrived =
            !!centre && Math.hypot(centre.x - 0.5, centre.y - 0.5) < UNREACHABLE_OFFSET;
          if (arrived || live.zoom < unreachableZoomRef.current - RETRY_ZOOM_DEG) {
            framingTarget.setUnreachable(false);
          }
        }
      }
      while (history.length > 2 && history[1].t < now - HISTORY_MS) history.shift();
      const ptz = vcamAt(history, now - VIDEO_LAG_MS);
      if (ptz && ptz.zoom > 0) {
        const dot = projectToVideo(selectedCamera, ptz, target);
        const ground =
          target.z > 0.05 ? projectToVideo(selectedCamera, ptz, { ...target, z: 0 }) : null;
        setProjected((prev) => {
          if (!dot) return null;
          const same =
            prev &&
            Math.abs(prev.dot.x - dot.x) < 1e-4 &&
            Math.abs(prev.dot.y - dot.y) < 1e-4 &&
            (prev.ground?.y ?? -1) === (ground?.y ?? -1);
          return same ? prev : { dot, ground };
        });
      }
      frame = requestAnimationFrame(update);
    };
    update();
    return () => cancelAnimationFrame(frame);
  }, [aiming, target, selectedCamera]);

  // Height from where the pointer is over the video, on the target's vertical line.
  const heightAt = (e: PointerEvent) => {
    const ptz = getCurrentPTZ?.();
    const current = framingTarget.get().target;
    if (!rootRef.current || !selectedCamera || !ptz || !current) return null;
    const rect = rootRef.current.getBoundingClientRect();
    return heightAtVideoPoint(selectedCamera, ptz, current, {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    });
  };
  const heightAtRef = useRef(heightAt);
  heightAtRef.current = heightAt;

  // Native listeners, not React's: the video pans the camera on drag, and
  // on arrow keys too (WheelZoom binds use-gesture natively on an ancestor),
  // so the dot has to stop its events before they bubble there, and React
  // only sees them at the root.
  useEffect(() => {
    const el = dotRef.current;
    if (!el) return;
    const down = (e: PointerEvent) => {
      e.stopPropagation();
      e.preventDefault();
      // preventDefault also skips focusing the dot, which the arrow keys need.
      el.focus({ preventScroll: true });
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        // Synthetic pointers can't be captured; the drag still works over the dot.
      }
      stopRef.current();
      framingTarget.setDragging(true);
    };
    const move = (e: PointerEvent) => {
      if (!framingTarget.get().dragging) return;
      e.stopPropagation();
      const z = heightAtRef.current(e);
      if (z != null) framingTarget.setHeight(z);
    };
    const up = (e: PointerEvent) => {
      if (!framingTarget.get().dragging) return;
      e.stopPropagation();
      framingTarget.setDragging(false);
    };
    const key = (e: KeyboardEvent) => {
      const z = framingTarget.get().target?.z;
      if (z == null) return;
      const step = e.shiftKey ? KEY_STEP_LARGE_FT : KEY_STEP_FT;
      if (e.key === 'ArrowUp') framingTarget.setHeight(z + step);
      else if (e.key === 'ArrowDown') framingTarget.setHeight(z - step);
      else if (e.key === 'Home') framingTarget.setHeight(0);
      else return;
      e.preventDefault();
      e.stopPropagation();
    };
    const keyUp = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'Home') e.stopPropagation();
    };
    const swallow = (e: Event) => e.stopPropagation();
    el.addEventListener('keydown', key);
    el.addEventListener('keyup', keyUp);
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    el.addEventListener('mousedown', swallow);
    el.addEventListener('mouseup', swallow);
    return () => {
      el.removeEventListener('keydown', key);
      el.removeEventListener('keyup', keyUp);
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
      el.removeEventListener('mousedown', swallow);
      el.removeEventListener('mouseup', swallow);
    };
  }, [projected != null]);

  if (!aiming || !target || !projected) return null;

  const pct = (v: number) => `${v * 100}%`;
  const { dot: aimed, ground: groundAt } = projected;
  // Off the frame (a spot the camera can't turn to), pin the dot to the edge
  // it is beyond, so there is still something pointing at it.
  const offFrame = aimed.x < 0 || aimed.x > 1 || aimed.y < 0 || aimed.y > 1;
  const dot = {
    x: offFrame ? Math.min(Math.max(aimed.x, EDGE_INSET_X), 1 - EDGE_INSET_X) : aimed.x,
    y: offFrame ? Math.min(Math.max(aimed.y, EDGE_INSET_TOP), 1 - EDGE_INSET_BOTTOM) : aimed.y,
  };
  const ground = offFrame ? null : groundAt;
  // Near the right edge the label goes on the left of the dot.
  const labelLeft = dot.x > LABEL_FLIP_X;
  const outOfReach = target.z > HIGH_TARGET_FT ? 'too high for this zoom' : 'out of reach';
  // Off the frame is normal while the camera is still turning towards the
  // spot: only the faded edge dot shows then. "Out of reach" waits until the
  // drive has actually given up.
  const showLabel = dragging || unreachable || !offFrame;

  return (
    <Box
      ref={rootRef}
      data-testid="target-video-overlay"
      sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 6 }}
    >
      <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
        {ground && (
          <>
            <line
              x1={pct(ground.x)}
              y1={pct(ground.y)}
              x2={pct(dot.x)}
              y2={pct(dot.y)}
              stroke={TARGET_COLOR}
              strokeWidth={2}
              strokeDasharray="5 4"
            />
            <ellipse
              cx={pct(ground.x)}
              cy={pct(ground.y)}
              rx={10}
              ry={4}
              fill="none"
              stroke={TARGET_COLOR}
              strokeWidth={2}
            />
          </>
        )}
      </svg>
      <Box
        ref={dotRef}
        data-testid="target-dot"
        role="slider"
        tabIndex={0}
        aria-label="Target height"
        aria-orientation="vertical"
        aria-valuemin={0}
        aria-valuemax={MAX_TARGET_HEIGHT}
        aria-valuenow={Math.round(target.z * 10) / 10}
        aria-valuetext={`${target.z.toFixed(0)} feet above the ice`}
        sx={{
          position: 'absolute',
          left: `calc(${pct(dot.x)} - ${DOT_SIZE / 2}px)`,
          top: `calc(${pct(dot.y)} - ${DOT_SIZE / 2}px)`,
          width: DOT_SIZE,
          height: DOT_SIZE,
          borderRadius: '50%',
          border: '2px solid #fff',
          backgroundColor: TARGET_COLOR,
          boxShadow: '0 0 0 4px rgba(255, 176, 32, 0.35), 0 1px 4px rgba(0,0,0,0.6)',
          cursor: dragging ? 'grabbing' : 'ns-resize',
          // Height is dragged along the target's line, which is off the frame.
          pointerEvents: offFrame ? 'none' : 'auto',
          opacity: offFrame ? 0.6 : 1,
          touchAction: 'none',
          outline: 'none',
          '&:focus-visible': { boxShadow: '0 0 0 4px rgba(255, 176, 32, 0.35), 0 0 0 6px #fff' },
        }}
      />
      {showLabel && (
        <Box
          sx={{
            position: 'absolute',
            ...(labelLeft
              ? { right: `calc(${pct(1 - dot.x)} + ${DOT_SIZE}px)` }
              : { left: `calc(${pct(dot.x)} + ${DOT_SIZE}px)` }),
            top: `calc(${pct(dot.y)} - 10px)`,
            px: 0.75,
            borderRadius: 0.5,
            fontSize: 11,
            lineHeight: '20px',
            color: '#fff',
            backgroundColor: 'rgba(16, 16, 19, 0.75)',
            whiteSpace: 'nowrap',
          }}
        >
          {dragging
            ? `${target.z.toFixed(1)} ft`
            : unreachable
              ? `${target.z.toFixed(0)} ft · ${outOfReach}`
              : preset
                ? `${preset.name} · ${target.z.toFixed(0)} ft`
                : `${target.z.toFixed(0)} ft · drag to ${target.z > 0.05 ? 'adjust' : 'raise'}`}
        </Box>
      )}
    </Box>
  );
};

export default TargetVideoOverlay;
